import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useFitStore } from '../store/useFitStore';
import Card from '../components/shared/Card';
import Button from '../components/shared/Button';
import StripeConnectCard from '../components/coach/StripeConnectCard';
import PlansManager from '../components/coach/PlansManager';
import { getDefaultUnit, setDefaultUnit } from '../lib/sets';
import { planPriceLabel } from '../lib/coachingPlans';
import type { WeightUnit } from '../store/types';

const Profile: React.FC = () => {
  const { user, achievements, coachingPlans, signOut, updateUserPrivacy, regenerateFeedToken, startTour } = useFitStore();
  const navigate = useNavigate();
  const [showPlans, setShowPlans] = useState(false);
  const replayTour = () => {
    if (user) localStorage.removeItem(`fitflex_tour_done:${user.id}`);
    navigate('/coach');
    startTour();
  };
  const [copied, setCopied] = useState(false);
  const [confirmRegen, setConfirmRegen] = useState(false);
  const [unit, setUnit] = useState<WeightUnit>(getDefaultUnit());
  const pickUnit = (u: WeightUnit) => { setDefaultUnit(u); setUnit(u); };

  const isCoach = user?.role === 'coach' || user?.role === 'owner';
  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL as string | undefined;
  const feedHttps = isCoach && user?.calendarFeedToken && supabaseUrl
    ? `${supabaseUrl}/functions/v1/ics-feed?token=${user.calendarFeedToken}`
    : '';
  const feedWebcal = feedHttps.replace(/^https?:\/\//, 'webcal://');

  const copyFeed = async () => {
    try {
      await navigator.clipboard.writeText(feedHttps);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      setCopied(false);
    }
  };

  return (
    <div className="main-content">
      <header style={{ marginBottom: 'var(--spacing-xl)', textAlign: 'center' }}>
        <div style={{ 
          width: '80px', 
          height: '80px', 
          background: 'var(--bg-tertiary)', 
          borderRadius: '50%', 
          margin: '0 auto var(--spacing-md)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '2rem',
          border: '2px solid var(--accent-primary)'
        }}>
          {user?.name[0].toUpperCase()}
        </div>
        <h1 style={{ fontSize: '1.8rem', fontWeight: 900 }}>{user?.name}</h1>
        <p className="text-muted" style={{ fontSize: '0.9rem' }}>{user?.role.toUpperCase()} • {user?.email}</p>
      </header>

      <section style={{ marginBottom: 'var(--spacing-xl)' }}>
        <h2 style={{ fontSize: '1.1rem', fontWeight: 800, color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: 'var(--spacing-md)' }}>
          Privacy Settings
        </h2>
        <Card variant="glass" padding="var(--spacing-md)" onClick={() => updateUserPrivacy(!user?.isIncognito)} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }}>
          <div>
            <p style={{ fontWeight: 700 }}>Incognito Mode</p>
            <p className="text-muted" style={{ fontSize: '0.7rem' }}>Hide your wins from the community feed</p>
          </div>
          <div style={{ 
            width: '40px', height: '20px', background: user?.isIncognito ? 'var(--accent-primary)' : 'var(--bg-tertiary)', 
            borderRadius: '10px', position: 'relative', transition: 'background 0.3s ease' 
          }}>
            <div style={{ 
              width: '16px', height: '16px', background: 'white', borderRadius: '50%', 
              position: 'absolute', top: '2px', left: user?.isIncognito ? '22px' : '2px',
              transition: 'left 0.3s ease'
            }} />
          </div>
        </Card>
      </section>

      {isCoach && (
        <section style={{ marginBottom: 'var(--spacing-xl)' }}>
          <h2 style={{ fontSize: '1.1rem', fontWeight: 800, color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: 'var(--spacing-md)' }}>
            Training Units
          </h2>
          <Card variant="glass" padding="var(--spacing-md)" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--spacing-xl)' }}>
            <div>
              <p style={{ fontWeight: 700 }}>Default weight unit</p>
              <p className="text-muted" style={{ fontSize: '0.7rem' }}>New exercises use this — each exercise can still override it</p>
            </div>
            <div style={{ display: 'flex', gap: '4px', background: 'var(--bg-tertiary)', borderRadius: '999px', padding: '2px' }}>
              {(['lb', 'kg'] as WeightUnit[]).map((u) => (
                <button
                  key={u}
                  onClick={() => pickUnit(u)}
                  style={{
                    padding: '4px 14px', borderRadius: '999px', fontSize: '0.75rem', fontWeight: 700,
                    background: unit === u ? 'white' : 'transparent',
                    color: unit === u ? 'var(--bg-primary, #000)' : 'var(--text-secondary)',
                    transition: 'all 0.2s ease',
                  }}
                >
                  {u}
                </button>
              ))}
            </div>
          </Card>
          <Card variant="glass" padding="var(--spacing-md)" onClick={replayTour} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--spacing-xl)', cursor: 'pointer' }}>
            <div>
              <p style={{ fontWeight: 700 }}>Replay the product tour</p>
              <p className="text-muted" style={{ fontSize: '0.7rem' }}>90 seconds — including the features that are easy to miss</p>
            </div>
            <span style={{ fontSize: '1rem' }}>▸</span>
          </Card>
          <h2 style={{ fontSize: '1.1rem', fontWeight: 800, color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: 'var(--spacing-md)' }}>
            Billing
          </h2>
          <div style={{ marginBottom: 'var(--spacing-md)' }}>
            <StripeConnectCard />
          </div>
          <Card variant="glass" padding="var(--spacing-md)" onClick={() => setShowPlans(true)} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--spacing-xl)', cursor: 'pointer' }}>
            <div>
              <p style={{ fontWeight: 700 }}>Pricing plans</p>
              <p className="text-muted" style={{ fontSize: '0.7rem' }}>
                {coachingPlans.length > 0
                  ? coachingPlans.filter((p) => p.isActive).map((p) => `${p.name} ${planPriceLabel(p)}`).join(' · ') || 'All plans hidden'
                  : 'Define plans once, enroll clients from their profile'}
              </p>
            </div>
            <span style={{ fontSize: '1rem' }}>▸</span>
          </Card>
          <h2 style={{ fontSize: '1.1rem', fontWeight: 800, color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: 'var(--spacing-md)' }}>
            Calendar Feed
          </h2>
          <Card variant="glass" padding="var(--spacing-md)">
            <p className="text-muted" style={{ fontSize: '0.8rem', marginBottom: 'var(--spacing-md)' }}>
              Subscribe to your appointments from Google, Apple, or Outlook Calendar. Paste this link wherever your
              calendar asks to “subscribe by URL” — it stays in sync automatically.
            </p>
            {feedHttps ? (
              <>
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center', marginBottom: 'var(--spacing-md)' }}>
                  <input readOnly value={feedHttps} className="input" style={{ flex: '1 1 240px', fontSize: '0.72rem' }} onFocus={(e) => e.currentTarget.select()} />
                  <button onClick={copyFeed} className="btn btn-glass btn-sm">{copied ? 'Copied ✓' : 'Copy'}</button>
                  <a href={feedWebcal} className="btn btn-primary btn-sm" style={{ textDecoration: 'none' }}>Subscribe</a>
                </div>
                {confirmRegen ? (
                  <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                    <span className="text-muted" style={{ fontSize: '0.72rem' }}>Regenerate? This breaks existing subscriptions.</span>
                    <button onClick={() => { regenerateFeedToken(); setConfirmRegen(false); }} style={{ fontSize: '0.72rem', fontWeight: 700, color: 'white', textDecoration: 'underline' }}>Yes, regenerate</button>
                    <button onClick={() => setConfirmRegen(false)} className="text-muted" style={{ fontSize: '0.72rem' }}>Cancel</button>
                  </div>
                ) : (
                  <button onClick={() => setConfirmRegen(true)} className="text-muted" style={{ fontSize: '0.72rem', textDecoration: 'underline' }}>
                    Regenerate link
                  </button>
                )}
              </>
            ) : (
              <p className="text-muted" style={{ fontSize: '0.75rem' }}>Your feed link will appear here once your account finishes setting up.</p>
            )}
          </Card>
        </section>
      )}

      <section style={{ marginBottom: 'var(--spacing-xl)' }}>
        <h2 style={{ fontSize: '1.1rem', fontWeight: 800, color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: 'var(--spacing-md)' }}>
          Trophy Room
        </h2>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--spacing-md)' }}>
          {achievements.length > 0 ? achievements.map((ach) => (
            <Card key={ach.id} variant="glass" padding="var(--spacing-md)" style={{ textAlign: 'center', border: '1px solid var(--accent-primary)' }}>
              <div style={{ fontSize: '2.5rem', marginBottom: '8px' }}>{ach.icon}</div>
              <p style={{ fontWeight: 800, fontSize: '0.8rem' }}>{ach.title.toUpperCase()}</p>
              <p className="text-muted" style={{ fontSize: '0.6rem' }}>{new Date(ach.unlockedAt).toLocaleDateString()}</p>
            </Card>
          )) : (
            <div style={{ gridColumn: '1 / -1', textAlign: 'center', padding: 'var(--spacing-lg)' }}>
              <p className="text-muted">No trophies yet. Keep training!</p>
            </div>
          )}
        </div>
      </section>

      <Button variant="danger" fullWidth onClick={signOut}>LOGOUT</Button>

      {showPlans && <PlansManager onClose={() => setShowPlans(false)} />}

      <div style={{ display: 'flex', justifyContent: 'center', gap: '18px', marginTop: 'var(--spacing-lg)' }}>
        <Link to="/privacy" className="text-muted" style={{ fontSize: '0.72rem', textDecoration: 'none' }}>Privacy</Link>
        <Link to="/terms" className="text-muted" style={{ fontSize: '0.72rem', textDecoration: 'none' }}>Terms</Link>
        <Link to="/transparency" className="text-muted" style={{ fontSize: '0.72rem', textDecoration: 'none' }}>AI &amp; Data</Link>
      </div>
    </div>
  );
};

export default Profile;
